import React, { useState, useEffect } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const EstimatedYield = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalYield, setTotalYield] = useState(0);
  const [yieldPerFarmer, setYieldPerFarmer] = useState([]);
  const [yieldPerCrop, setYieldPerCrop] = useState([]);

  const [viewType, setViewType] = useState("farmer");
  const [selectedFarmer, setSelectedFarmer] = useState("all");
  const [selectedCrop, setSelectedCrop] = useState("all");
  const [chartType, setChartType] = useState("pie");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const url = `/api/totalEstimatedYieldProcurement/?userId=${localStorage.getItem(
        "id")}`;
    fetchData({ url }, (res) => {
      setTotalYield(res.totalEstimatedYield || 0);
      setYieldPerFarmer(res.yieldPerFarmer || []);
      setYieldPerCrop(res.yieldPerCrop || []);
      setLoading(false);
    });
  }, [fetchData]);

  let chartDataset = [];

  if (viewType === "farmer") {
    chartDataset =
      selectedFarmer === "all"
        ? yieldPerFarmer
        : yieldPerFarmer.filter(
            (f) => String(f.farmerId) === String(selectedFarmer)
          );
  } else {
    chartDataset =
      selectedCrop === "all"
        ? yieldPerCrop
        : yieldPerCrop.filter((c) => c.cropName === selectedCrop);
  }

  let displayAmount = totalYield;

  if (viewType === "farmer" && selectedFarmer !== "all") {
    const row = yieldPerFarmer.find(
      (f) => String(f.farmerId) === String(selectedFarmer)
    );
    displayAmount = row ? row.totalYield : 0;
  }

  if (viewType === "crop" && selectedCrop !== "all") {
    const rows = yieldPerCrop.filter((c) => c.cropName === selectedCrop);
    displayAmount = rows.reduce(
      (sum, r) => sum + (r.totalYield || 0),
      0
    );
  }

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: "20px",
          background: "#c2d5aa",
          borderRadius: "14px",
        }}
      >
        <div className="flex justify-between mb-5">

          <Select
            value={viewType}
            style={{ width: 180 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedCrop("all");
              setSelectedFarmer("all");
            }}
          >
            <Option value="farmer">Farmer Wise</Option>
            <Option value="crop">Crop Wise</Option>
          </Select>

          {viewType === "farmer" && (
            <Select
              value={selectedFarmer}
              style={{ width: 220 }}
              onChange={(v) => setSelectedFarmer(v)}
            >
              <Option value="all">All Farmers</Option>
              {yieldPerFarmer.map((f) => (
                <Option key={f.farmerId} value={f.farmerId}>
                  {f.farmerName}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "crop" && (
            <Select
              value={selectedCrop}
              style={{ width: 220 }}
              onChange={(v) => setSelectedCrop(v)}
            >
              <Option value="all">All Crops</Option>
              {yieldPerCrop.map((c, i) => (
                <Option key={i} value={c.cropName}>
                  {c.cropName}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160 }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        <div
          style={{
            textAlign: "center",
            marginBottom: "18px",
            padding: "14px",
            background: "#d6efd3",
            borderRadius: "12px",
          }}
        >
          <h1 style={{ fontSize: "40px", fontWeight: "700", color: "#1f4e1e" }}>
            {displayAmount.toLocaleString()} Kg
          </h1>

          <p style={{ fontSize: "18px", color: "#1f4e1e" }}>
            {viewType === "farmer"
              ? selectedFarmer === "all"
                ? "Total Estimated Yield"
                : "Yield (Selected Farmer)"
              : selectedCrop === "all"
                ? "Total Estimated Yield"
                : "Yield (Selected Crop)"}
          </p>
        </div>

        {chartType === "pie" && (
          <DynamicPieChart
            data={chartDataset}
            angleField="totalYield"
            colorField={viewType === "farmer" ? "farmerName" : "cropName"}
            isMaximized={true}
          />
        )}

        {chartType === "column" && (
          <DynamicColumnChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalYield"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}

        {chartType === "bar" && (
          <DynamicBarChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalYield"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}

        {chartType === "line" && (
          <DynamicLineChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalYield"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: "200px",
        width: "100%",
        padding: "18px",
        position: "relative",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "700",
                color: "#3d5c26",
              }}
            >
              {totalYield.toLocaleString()} Kg
            </h1>

            <p
              style={{
                fontSize: "17px",
                marginTop: "-6px",
                color: "#3d5c26",
              }}
            >
              Estimated Yield
            </p>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "18px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#3d5c26",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default EstimatedYield;
